import { countryName } from "@/lib/geo"
import { formatMoney } from "@/lib/format"
import type { FieldType } from "./api"

/** Values and labels exactly as Shopify writes them in Analytics. */
export function formatValue(v: number | null | undefined, type: FieldType, opts: { compact?: boolean } = {}): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "—"
  switch (type) {
    case "money":
      if (opts.compact && Math.abs(v) >= 10000) return `${v < 0 ? "-" : ""}$${compact(Math.abs(v))}`
      return formatMoney(v)
    case "percent":
      return `${v.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`
    case "duration":
      return duration(v)
    case "decimal":
      return v.toLocaleString("en-US", { maximumFractionDigits: 3 })
    case "ratio":
      // ROAS and similar: always two decimals (2.00, not 2).
      return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    default:
      return opts.compact && Math.abs(v) >= 10000 ? compact(v) : v.toLocaleString("en-US", { maximumFractionDigits: 2 })
  }
}

/** Short axis labels: $4K, $2.5K, 12%, 1.2K. */
export function axisValue(v: number, type: FieldType): string {
  const c = (x: number) => new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(x)
  if (type === "money") return `${v < 0 ? "-" : ""}$${c(Math.abs(v))}`
  if (type === "percent") return `${c(v)}%`
  if (type === "duration") return duration(v)
  return c(v)
}

const compact = (v: number) => new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(v)

export function duration(sec: number) {
  if (sec < 60) return `${Math.round(sec)}s`
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.round((sec % 3600) / 60)}m`
  return `${Math.floor(sec / 86400)}d ${Math.round((sec % 86400) / 3600)}h`
}

/** % change like Shopify: null when there's nothing to compare against. */
export function change(cur: number | null | undefined, prev: number | null | undefined): number | null {
  if (cur == null || prev == null) return null
  if (prev === 0) return cur === 0 ? 0 : null
  return Math.round(((cur - prev) / Math.abs(prev)) * 1000) / 10
}

const WORDS: Record<string, string> = {
  // referrer sources / traffic types
  direct: "Direct",
  search: "Search",
  social: "Social",
  email: "Email",
  unknown: "Unknown",
  paid: "Paid",
  organic: "Organic",
  // devices
  mobile: "Mobile",
  desktop: "Desktop",
  tablet: "Tablet",
  smart_tv: "Smart TV",
  other: "Other",
  // page types
  homepage: "Homepage",
  product: "Product",
  collection: "Collection",
  cart: "Cart",
  checkout: "Checkout",
  page: "Custom Page",
  policy: "Policy",
  blog: "Blog",
  article: "Article",
  account: "Account",
  // misc
  human: "Human",
  bot: "Bot",
  true: "Yes",
  false: "No",
  first_time: "New",
  returning: "Returning",
  one_time: "One-time",
  repeat: "Repeat",
  order: "Order",
  refund: "Refund",
  cancel: "Cancellation",
  fulfilled: "Fulfilled",
  unfulfilled: "Unfulfilled",
  pending: "Pending",
  completed: "Completed",
  canceled: "Canceled",
  archived: "Archived",
  sale: "Sale",
  card: "Card",
  gift_card: "Gift card",
  pp_system_default: "Manual",
}
const DOW = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** Display label for a dimension value. */
export function dimLabel(dim: string, v: unknown): string {
  if (v === null || v === undefined || v === "") return "None"
  const s = String(v)
  if (dim === "day_of_week") return DOW[Number(v)] ?? s
  if (dim === "hour_of_day") return hourLabel(Number(v))
  if (["hour", "day", "week", "month", "quarter", "year", "first_order_month"].includes(dim)) return timeLabel(dim === "first_order_month" ? "month" : dim, s)
  if (/(^|_)country$/.test(dim)) return s === "None" ? s : countryName(s) || s
  if (dim === "payment_gateway") return WORDS[s] ?? s.replace(/^pp_/, "").replace(/_[a-z]+$/, "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())
  if (WORDS[s] && !["product_title", "search_query", "customer_name", "landing_page_path", "page_path", "page_url", "referrer_name", "marketing_channel", "order_marketing_channel", "order_referrer_name"].includes(dim)) return WORDS[s]
  return s
}

const hourLabel = (h: number) => `${h % 12 || 12} ${h < 12 ? "AM" : "PM"}`

/** "2026-10-25" → "Oct 25", "2026-10" → "Oct 2026", "2026-10-25 14" → "2 PM". */
export function timeLabel(grain: string, key: string, opts: { long?: boolean } = {}): string {
  if (grain === "hour") {
    const [d, h] = key.split(" ")
    const base = hourLabel(Number(h))
    return opts.long ? `${timeLabel("day", d, opts)}, ${base}` : base
  }
  if (grain === "day" || grain === "week") {
    const [y, m, d] = key.split("-").map(Number)
    const s = `${MON[m - 1]} ${d}`
    return opts.long ? `${grain === "week" ? "Week of " : ""}${s}, ${y}` : s
  }
  if (grain === "month") {
    const [y, m] = key.split("-").map(Number)
    return `${MON[m - 1]} ${y}`
  }
  if (grain === "quarter") return key.replace(/^(\d{4})-Q(\d)$/, "Q$2 $1")
  return key
}

export function rangeLabel(since: string, until: string) {
  const f = (s: string) => {
    const [y, m, d] = s.split("-").map(Number)
    return { y, t: `${MON[m - 1]} ${d}` }
  }
  const a = f(since)
  const b = f(until)
  if (since === until) return `${a.t}, ${a.y}`
  return a.y === b.y ? `${a.t}–${b.t}, ${b.y}` : `${a.t}, ${a.y}–${b.t}, ${b.y}`
}

export const relTime = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).toLowerCase()
